import React from 'react';
import Modal from '@/components/Modal';
import BasicButton from '@/components/styled/BasicButton';
import { getRecentUpdates, AppUpdate } from '@/config/appUpdates';
import { useTranslation } from 'react-i18next';
import { track } from '@/analytics';
import classNames from 'classnames';
import { formatDateOptimized } from '@/utils/dateUtils';

interface WhatsNewModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export function WhatsNewModal({ isOpen, onClose }: WhatsNewModalProps) {
    const { t } = useTranslation();
    const [selectedUpdate, setSelectedUpdate] = React.useState<AppUpdate | null>(null);

    const recentUpdates = React.useMemo(() => getRecentUpdates(10), []);

    React.useEffect(() => {
        // Select the first update by default
        if (recentUpdates.length > 0 && !selectedUpdate) {
            setSelectedUpdate(recentUpdates[0]);
        }
    }, [recentUpdates, selectedUpdate]);

    // Clean up state when modal closes to prevent memory leaks
    React.useEffect(() => {
        if (!isOpen) {
            const timer = setTimeout(() => setSelectedUpdate(null), 300);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [isOpen]);

    function handleUpdateClick(update: AppUpdate) {
        setSelectedUpdate(update);
        track(`whats-new-viewed-${update.key}`);
    }

    function handleLearnMore() {
        if (selectedUpdate?.actionUrl) {
            track(`whats-new-action-${selectedUpdate.key}`);
            window.open(selectedUpdate.actionUrl, '_blank');
        }
    }

    // Don't render modal content if not open to save memory
    if (!isOpen) return null;

    return (
        <Modal.Large isOpen={isOpen} onClose={onClose}>
            <div className="flex h-[90vh] max-h-[600px] bg-white dark:bg-zinc-800 rounded-lg">
                {/* Left sidebar with update list */}
                <div className="w-1/3 border-r border-gray-200 dark:border-zinc-600 overflow-y-auto bg-gray-50 dark:bg-zinc-900 rounded-l-lg">
                    <div className="p-4">
                        <h2 className="text-lg font-semibold text-saga-black dark:text-zinc-200 mb-4">
                            {t('whats_new.title')}
                        </h2>
                        <div className="space-y-2">
                            {recentUpdates.map((update) => (
                                <button
                                    key={update.key}
                                    onClick={() => handleUpdateClick(update)}
                                    className={classNames(
                                        'w-full text-left p-3 rounded-lg transition-colors',
                                        'hover:bg-saga-gray-100 dark:hover:bg-zinc-700',
                                        {
                                            'bg-saga-gray-100 dark:bg-zinc-700': selectedUpdate?.key === update.key,
                                        },
                                    )}
                                    aria-label={`View update: ${update.title}`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <h3 className="font-medium text-saga-black dark:text-zinc-200">
                                                {update.title}
                                            </h3>
                                            <p className="text-xs text-saga-gray-dark dark:text-zinc-400 mt-1">
                                                {formatDateOptimized(update.releaseDate, undefined)}
                                            </p>
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right content area */}
                <div className="flex-1 flex flex-col">
                    {selectedUpdate ? (
                        <>
                            {selectedUpdate.image && (
                                <div className="h-48">
                                    <img
                                        src={selectedUpdate.image}
                                        alt={selectedUpdate.title}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).style.display = 'none';
                                        }}
                                    />
                                </div>
                            )}

                            <div className="flex-1 p-8">
                                <h1 className="text-2xl font-semibold text-saga-black dark:text-zinc-200 mb-4">
                                    {selectedUpdate.title}
                                </h1>

                                <p className="text-saga-gray-dark dark:text-zinc-400 mb-6">
                                    {selectedUpdate.description}
                                </p>

                                {selectedUpdate.actionUrl && (
                                    <div className="flex justify-end">
                                        <BasicButton onClick={handleLearnMore} variant="primary" className="px-5 py-3">
                                            {t('common.learn_more')}
                                        </BasicButton>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex items-center justify-center text-saga-gray-dark dark:text-zinc-400">
                            {t('whats_new.no_selection')}
                        </div>
                    )}
                </div>
            </div>

            <Modal.CloseButton />
        </Modal.Large>
    );
}
